import React, { useState, useRef, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import 'altcha';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  
  const [captchaToken, setCaptchaToken] = useState(null);
  const altchaRef = useRef(null);
  
  // Check if we're in development mode (captcha optional) vs production (captcha required)
  const isDevelopmentMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  useEffect(() => {
    const altchaWidget = altchaRef.current;
    
    const handleStateChange = (ev) => {
      if (ev.detail.state === 'verified') {
        setCaptchaToken(ev.detail.payload);
      } else {
        setCaptchaToken(null);
      }
    };

    if (altchaWidget) {
      altchaWidget.addEventListener('statechange', handleStateChange);
      
      return () => {
        altchaWidget.removeEventListener('statechange', handleStateChange);
      };
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require captcha in production, optional in development
    if (!isDevelopmentMode && !captchaToken) {
      setStatus({ 
        loading: false, 
        success: false, 
        error: 'Veuillez compléter la vérification CAPTCHA.'
      });
      return;
    }
    
    setStatus({ loading: true, success: false, error: null });

    try {
      const response = await fetch('https://api.aikido-cahors.fr/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(captchaToken && { captcha: captchaToken })
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ firstName: '', lastName: '', email: '', message: '' });
        setCaptchaToken(null);
        // Reset the captcha widget if it exists
        if (altchaRef.current) {
          altchaRef.current.reset();
        }
      } else {
        setStatus({ 
          loading: false, 
          success: false, 
          error: result.message || 'Une erreur est survenue lors de l\'envoi du message.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ 
        loading: false, 
        success: false, 
        error: 'Impossible de se connecter au serveur. Veuillez réessayer plus tard.'
      });
    }
  };

  return (
    <div>
      {/* Success Message */}
      {status.success && (
        <div className="mb-6 p-4 rounded-lg flex items-center gap-3" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}>
          <CheckCircle size={24} style={{ color: '#155724' }} />
          <div>
            <p className="font-medium" style={{ color: '#155724' }}>Message envoyé avec succès !</p>
            <p className="text-sm" style={{ color: '#155724' }}>Nous vous répondrons dans les plus brefs délais.</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {status.error && (
        <div className="mb-6 p-4 rounded-lg flex items-center gap-3" style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }}>
          <AlertCircle size={24} style={{ color: '#721c24' }} />
          <div>
            <p className="font-medium" style={{ color: '#721c24' }}>Erreur d'envoi</p>
            <p className="text-sm" style={{ color: '#721c24' }}>{status.error}</p>
            <p className="text-sm mt-1" style={{ color: '#721c24' }}>
              Ou contactez-nous à :{' '}
              <a href="mailto:contact@aikido-cahors.fr" className="underline font-medium">
                contact@aikido-cahors.fr
              </a>
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block body-small font-medium mb-2" style={{ color: 'var(--text-on-light)' }}>
              Prénom *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={status.loading}
              className="w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:ring-opacity-50 disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-card-light)',
                borderColor: 'var(--border-medium)',
                color: 'var(--text-on-light)',
                ...(formData.firstName && { borderColor: 'var(--brand-primary)' })
              }}
              placeholder="Votre prénom"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block body-small font-medium mb-2" style={{ color: 'var(--text-on-light)' }}>
              Nom *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={status.loading}
              className="w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:ring-opacity-50 disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-card-light)',
                borderColor: 'var(--border-medium)',
                color: 'var(--text-on-light)',
                ...(formData.lastName && { borderColor: 'var(--brand-primary)' })
              }}
              placeholder="Votre nom"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="email" className="block body-small font-medium mb-2" style={{ color: 'var(--text-on-light)' }}>
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={status.loading}
            className="w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:ring-opacity-50 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--bg-card-light)',
              borderColor: 'var(--border-medium)',
              color: 'var(--text-on-light)',
              ...(formData.email && { borderColor: 'var(--brand-primary)' })
            }}
            placeholder="votre.email@exemple.com"
          />
        </div>
        
        <div>
          <label htmlFor="message" className="block body-small font-medium mb-2" style={{ color: 'var(--text-on-light)' }}>
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="4"
            disabled={status.loading}
            className="w-full p-3 border rounded-lg resize-none transition-colors focus:ring-2 focus:ring-opacity-50 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--bg-card-light)',
              borderColor: 'var(--border-medium)',
              color: 'var(--text-on-light)',
              ...(formData.message && { borderColor: 'var(--brand-primary)' })
            }}
            placeholder="N'hésitez-pas à nous expliquer ce que vous recherchez ou à nous poser toute autre question."
          ></textarea>
        </div>
        
        {!isDevelopmentMode && (
          <div>
            <altcha-widget
              ref={altchaRef}
              challengeurl="https://api.aikido-cahors.fr/api/altcha/challenge"
              style={{
                '--altcha-color-base': 'var(--bg-card-light)',
                '--altcha-color-text': 'var(--text-on-light)',
                '--altcha-color-accent': 'var(--brand-primary)',
                '--altcha-color-border': 'var(--border-medium)',
                '--altcha-border-radius': '8px'
              }}
            />
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={status.loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status.loading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send size={20} />
              Nous contacter
            </>
          )}
        </button>
        
        <p className="text-xs text-center" style={{ color: 'var(--text-secondary-light)' }}>
          * Champs obligatoires. Vos données personnelles sont protégées et ne seront utilisées que pour vous répondre.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
